import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch (err) {
      toast.error("Failed to send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-olive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-olive" />
          </div>
          <h1 className="font-serif text-2xl text-charcoal mb-4">Check Your Email</h1>
          <p className="text-charcoal/70 mb-6">
            We've sent a password reset link to <strong>{email}</strong>. 
            The link will expire in 1 hour.
          </p>
          <p className="text-sm text-charcoal/50 mb-6">
            Didn't receive the email? Check your spam folder or try again.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => setSent(false)}
              variant="outline"
              className="w-full"
            >
              Try a different email
            </Button>
            <Link to="/login">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
        <Link to="/login" className="inline-flex items-center text-olive hover:text-olive/80 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </Link>

        <div className="w-16 h-16 bg-olive/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-olive" />
        </div>

        <h1 className="font-serif text-2xl text-charcoal text-center mb-2">
          Forgot Password?
        </h1>
        <p className="text-charcoal/70 text-center mb-8">
          No worries! Enter your email and we'll send you a reset link.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="h-12"
              data-testid="forgot-password-email"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-olive hover:bg-olive/90"
            data-testid="forgot-password-submit"
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </Button>
        </form>

        <p className="text-center text-sm text-charcoal/60 mt-6">
          Remember your password?{" "}
          <Link to="/login" className="text-olive hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
